import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, Download, FileText, ExternalLink, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { ActivityTypeBadge, QueryChips, StatusCodeBadge, formatActivityDate, downloadCsv } from './activityShared';

const th = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap';
const inputCls = 'px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

export const UserActivityTab = ({ onOpenSession }) => {
  const { token, backendUrl } = useAuth();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ search: '', type: 'all', page_label: 'all', start_date: '', end_date: '' });
  const [sort, setSort] = useState('desc');
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const perPage = 50;

  const buildParams = useCallback((p, pp) => {
    const params = new URLSearchParams({ page: p, per_page: pp, sort });
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.append(k, v); });
    return params;
  }, [filters, sort]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${backendUrl}/api/audit/user-activity?${buildParams(page, perPage)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setPages(r.data.pages || []); })
        .catch(() => { setItems([]); setTotal(0); })
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [token, backendUrl, page, buildParams, refreshTick]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const toggleSort = () => { setSort((s) => (s === 'desc' ? 'asc' : 'desc')); setPage(1); };
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${backendUrl}/api/audit/user-activity?${buildParams(1, 1000)}`, { headers: { Authorization: `Bearer ${token}` } });
      const rows = (r.data.items || []).map((i) => [formatActivityDate(i.timestamp), i.email || '', i.type, i.page_label || '', i.action, Object.entries(i.query || {}).map(([k, v]) => `${k}=${v}`).join(' '), i.target_id || '', i.status_code, i.duration_ms, i.ip_address || '']);
      downloadCsv(`user_activity_${new Date().toISOString().slice(0, 10)}.csv`, ['Timestamp', 'User', 'Type', 'Menu', 'Action', 'Query', 'Target', 'Status', 'Duration (ms)', 'IP'], rows);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-activity-tab">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input data-testid="user-activity-search" value={filters.search} onChange={(e) => setFilter('search', e.target.value)}
                placeholder="Search by user email, API key name, menu, action, record id or filter value…" className={`${inputCls} w-full pl-10`} />
            </div>
            <select data-testid="user-activity-type-filter" value={filters.type} onChange={(e) => setFilter('type', e.target.value)} className={inputCls}>
              <option value="all">All types</option>
              <option value="page_view">Menu visits</option>
              <option value="query">Queries</option>
              <option value="change">Changes</option>
              <option value="ingest">API Ingest</option>
            </select>
            <select data-testid="user-activity-page-filter" value={filters.page_label} onChange={(e) => setFilter('page_label', e.target.value)} className={inputCls}>
              <option value="all">All menus</option>
              {pages.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input data-testid="user-activity-start-date" type="date" value={filters.start_date} onChange={(e) => setFilter('start_date', e.target.value)} className={inputCls} />
            <input data-testid="user-activity-end-date" type="date" value={filters.end_date} onChange={(e) => setFilter('end_date', e.target.value)} className={inputCls} />
            <Button data-testid="user-activity-refresh" onClick={() => setRefreshTick((t) => t + 1)} disabled={loading} variant="outline" className="flex items-center gap-2" title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button data-testid="user-activity-export" onClick={exportCsv} disabled={exporting || loading || items.length === 0} variant="outline" className="flex items-center gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span style={{ color: 'var(--text-color)' }}>User Activity</span>
            <span data-testid="user-activity-total" className="text-sm font-normal" style={{ color: 'var(--text-muted-color)' }}>{total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--button-color)' }} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12" data-testid="user-activity-empty">
              <FileText className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
              <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No activity found</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>Menu visits, queries, changes and API ingest calls will appear here.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" data-testid="user-activity-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={th} style={{ color: 'var(--filter-label-color)' }}>
                        <button data-testid="user-activity-sort-date" onClick={toggleSort} className="inline-flex items-center gap-1 uppercase hover:underline" title={`Sorted ${sort === 'desc' ? 'newest first' : 'oldest first'} — click to toggle`}>
                          Timestamp {sort === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                        </button>
                      </th>
                      {['User / API key', 'Type', 'Menu', 'Action', 'Query / Filters', 'Result', 'Session'].map((h) => (
                        <th key={h} className={th} style={{ color: 'var(--filter-label-color)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((it) => (
                      <tr key={it.id} className="hover:bg-gray-50" data-testid="user-activity-row">
                        <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>{formatActivityDate(it.timestamp)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                          {it.email || it.user_id}
                          {it.type === 'ingest' && it.platform_id && <div className="text-[11px]" style={{ color: 'var(--text-muted-color)' }}>{it.platform_id}{it.organization ? ` · ${it.organization}` : ''}</div>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap"><ActivityTypeBadge type={it.type} /></td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-color)' }}>{it.page_label || '—'}</td>
                        <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-color)' }}>
                          {it.action}{it.target_id && <span className="ml-1 font-mono text-xs" style={{ color: 'var(--text-muted-color)' }}>{it.target_id}</span>}
                        </td>
                        <td className="px-4 py-3 max-w-md"><QueryChips query={it.query} /></td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted-color)' }}><StatusCodeBadge code={it.status_code} /> <span className="ml-1">{it.duration_ms} ms</span></td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {it.session_id ? (
                            <button data-testid="user-activity-open-session" onClick={() => onOpenSession(it.session_id)} className="inline-flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: 'var(--button-color)' }}>
                              <ExternalLink className="h-3 w-3" /> Login detail
                            </button>
                          ) : <span className="text-xs" style={{ color: 'var(--text-muted-color)' }}>{it.type === 'ingest' ? 'API key' : '—'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="text-sm" style={{ color: 'var(--text-muted-color)' }}>Page {page} of {totalPages}</div>
                  <div className="flex gap-2">
                    <button data-testid="user-activity-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 border rounded-lg disabled:opacity-50" style={{ borderColor: 'var(--border-color)', color: 'var(--button-color)' }}>Previous</button>
                    <button data-testid="user-activity-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 border rounded-lg disabled:opacity-50" style={{ borderColor: 'var(--border-color)', color: 'var(--button-color)' }}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserActivityTab;
